import { useCallback, useEffect, useState } from 'react';
import {
  getCrmDeals,
  getCrmDealDetail,
  generateDealPersonas,
  saveCrmPersonas,
  type CrmDealSummary,
  type CrmDealDetail,
  type CrmPersonaPreview,
} from '../../lib/crmApi';
import { getApiErrorMessage } from '../../lib/apiErrors';
import { LoadingQuotesDialog } from '../LoadingQuotesDialog';

interface CrmDealPickerProps {
  onPersonasSaved: () => void;
}

function formatCurrency(amount: number | null): string {
  if (amount == null) return '—';
  return `$${amount.toLocaleString()}`;
}

export function CrmDealPicker({ onPersonasSaved }: CrmDealPickerProps) {
  const [deals, setDeals] = useState<CrmDealSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState<CrmDealDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [generatedPersonas, setGeneratedPersonas] = useState<CrmPersonaPreview[]>([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDeals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCrmDeals({ search: search || undefined, limit: 25 });
      setDeals(result.deals);
      setTotal(result.total);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadDeals().catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [loadDeals]);

  const selectDeal = async (dealId: string) => {
    setLoadingDetail(true);
    setError(null);
    setGeneratedPersonas([]);
    try {
      const detail = await getCrmDealDetail(dealId);
      setSelectedDeal(detail);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedDeal) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await generateDealPersonas(selectedDeal.id);
      setGeneratedPersonas(result.personas);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!selectedDeal || generatedPersonas.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await saveCrmPersonas(generatedPersonas, 'crm_deal', selectedDeal.id);
      setSelectedDeal(null);
      setGeneratedPersonas([]);
      onPersonasSaved();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (selectedDeal) {
    return (
      <div className="space-y-4">
        <LoadingQuotesDialog
          open={generating}
          title="Generating buyer personas"
          subtitle="Analyzing deal data with AI…"
        />
        <button
          type="button"
          onClick={() => {
            setSelectedDeal(null);
            setGeneratedPersonas([]);
          }}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          &larr; Back to deals
        </button>

        <div className="bg-white p-5 rounded-lg border">
          <h3 className="font-semibold text-gray-900 text-lg">{selectedDeal.name}</h3>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
            {selectedDeal.amount != null && (
              <span>Value: {formatCurrency(selectedDeal.amount)}</span>
            )}
            {selectedDeal.stageName && <span>Stage: {selectedDeal.stageName}</span>}
            {selectedDeal.closeDate && <span>Close: {selectedDeal.closeDate.split('T')[0]}</span>}
          </div>
          {selectedDeal.account && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium">{selectedDeal.account.name}</span>
              {selectedDeal.account.industry && (
                <span className="ml-2 text-gray-400">({selectedDeal.account.industry})</span>
              )}
            </div>
          )}

          {selectedDeal.contacts.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Contacts ({selectedDeal.contacts.length})
              </h4>
              <div className="space-y-2">
                {selectedDeal.contacts.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-900">
                      {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                    </span>
                    {c.title && <span className="text-gray-500">{c.title}</span>}
                    {c.role && (
                      <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                        {c.role}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-800 disabled:opacity-50"
            >
              {generating ? 'Generating personas...' : 'Generate buyer personas'}
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            {error}
          </div>
        )}

        {generatedPersonas.length > 0 && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">
              Generated personas ({generatedPersonas.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {generatedPersonas.map((p, i) => (
                <div key={i} className="bg-white p-4 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <div className="font-medium text-gray-900">{p.name}</div>
                      <div className="text-sm text-gray-500">{p.jobTitle}</div>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                      {p.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{p.agenda}</p>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-800 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save personas'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <LoadingQuotesDialog
        open={generating}
        title="Generating buyer personas"
        subtitle="Analyzing deal data with AI…"
      />
      <div>
        <input
          type="text"
          placeholder="Search deals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <LoadingQuotesDialog
          open
          title="Syncing your CRM deals"
          subtitle="Fetching the latest deals from your CRM…"
        />
      ) : deals.length === 0 ? (
        <div className="text-sm text-gray-500">
          {search ? 'No deals match your search.' : 'No open deals found.'}
        </div>
      ) : (
        <>
          <div className="text-xs text-gray-400">
            {total} open deal{total !== 1 ? 's' : ''}
          </div>
          <div className="space-y-2">
            {deals.map((deal) => (
              <button
                key={deal.id}
                type="button"
                onClick={() => selectDeal(deal.id)}
                disabled={loadingDetail}
                className="w-full text-left p-4 bg-white border rounded-lg hover:border-green-300 hover:bg-green-50/30 transition-colors disabled:opacity-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900">{deal.name}</div>
                    <div className="text-sm text-gray-600 mt-0.5">
                      {deal.account?.name ?? 'No account'}
                      {deal.account?.industry && (
                        <span className="text-gray-400"> · {deal.account.industry}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0 text-sm">
                    <div className="font-medium text-gray-900">{formatCurrency(deal.amount)}</div>
                    {deal.stageName && (
                      <div className="text-xs text-gray-500">{deal.stageName}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                  {deal.closeDate && <span>Close: {deal.closeDate.split('T')[0]}</span>}
                  <span>
                    {deal.contactCount} contact{deal.contactCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
